import tinycolor from "tinycolor2";
import { computed, ComputedRef } from "vue";
import { useStore } from "vuex";

import { CustomizationRecord } from "@/Customizations/v2/customizations";
import { Appointment } from "@/typings/scheduler";

export const useScheduleColors = () => {
  const store = useStore();
  const eventStatuses = {
    traveling: "traveling",
    late: "late",
    hold: "hold",
    available: "available_event",
    unavailable: "unavailable_event",
    accepted_pre_work: "accepted_pre_work"
  };

  const statusCustomizations: ComputedRef<{
    [k: string]: CustomizationRecord;
  }> = computed(() => {
    return store.getters.sortedAppointmentStatusCustomizations;
  });
  const getStatusColor = (status) => {
    switch (status) {
      case eventStatuses.accepted_pre_work:
        return "#44d2d5";
      case eventStatuses.traveling:
        return "#6875F5";
      case eventStatuses.late:
        return "#C81E1E";
      case eventStatuses.hold:
        return "#9CA3AF";
      case eventStatuses.available:
      case eventStatuses.unavailable:
        return "#9CA3AF";
      default:
        return (
          statusCustomizations.value[status]?.value ||
          statusCustomizations.value[status]?.default
        );
    }
  };
  const getBGColor = (status: string) => {
    const hex = getStatusColor(status);
    let lightenPct = 50;
    const color = tinycolor(hex);
    let lighterShade = color.lighten(lightenPct);
    let brightness = lighterShade.getBrightness();
    do {
      lightenPct = lightenPct - 2;
      lighterShade = tinycolor(hex).lighten(lightenPct);
      brightness = lighterShade.getBrightness();
    } while (lighterShade.toHexString() === "#ffffff" || brightness > 245);
    return lighterShade.toHexString();
  };
  const getTextColor = (status: string) => {
    const hex = getStatusColor(status);
    return tinycolor(hex).darken(30).toHexString();
  };
  const getEventStyles = (
    appointment?: Appointment,
    ghost?: boolean
  ): {
    classes: Array<string>;
    colors: { background: string; text: string; status: string };
  } => {
    const customizationName = getStatusCustomizationName(appointment, ghost);

    const eventStyles = {
      classes: [
        "px-2 py-1 text-xs hover:ring-1 rounded-lg hover:shadow"
      ] as string[],
      colors: {
        background: getBGColor(customizationName),
        text: getTextColor(customizationName),
        status: getStatusColor(customizationName)
      }
    };
    if (
      [eventStatuses.available, eventStatuses.hold].includes(customizationName)
    ) {
      eventStyles.classes.push("outline-dashed hover:outline-solid");
    }
    return eventStyles;
  };
  const getStatusCustomizationName = (
    appointment?: Appointment,
    ghost?: boolean
  ): string => {
    if (!appointment) {
      return eventStatuses.unavailable;
    }
    if (ghost) {
      return eventStatuses.hold;
    }
    switch (appointment.status) {
      case "completed":
        return appointment.job_invoice_revenue_cents > 0
          ? "compeleted_won_opportunity"
          : "compeleted_lost_opportunity";
      case "accepted": {
        if (appointment.travel_start_time) {
          return appointment.late ? "late" : "traveling";
        }
        return "accepted_pre_work";
      }
      case "pending": {
        return appointment.unassigned
          ? "pending_unassigned"
          : "pending_assigned";
      }
      default:
        // Running
        return appointment.status;
    }
  };

  return {
    statusCustomizations,
    getStatusColor,
    getBGColor,
    getTextColor,
    getEventStyles,
    getStatusCustomizationName
  };
};
